import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { sendOTP as sendOTPApi, normalizePhone } from '../../sendOtp';
import { verifyOtpAndSignIn } from '../../services/verifyOtpAndSignIn';
import AppIcon from '../../components/AppIcon';
import BackButton from '../../components/BackButton';
import OtpInput from '../../components/OtpInput';

const CustomerRegistration = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
  });
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [msegatId, setMsegatId] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsContent, setTermsContent] = useState('');
  const [termsLoading, setTermsLoading] = useState(false);
  const verifyInFlightRef = useRef(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // ── تحميل محتوى الشروط من Supabase ────────────────────────────────────────
  const openTermsModal = async () => {
    setShowTermsModal(true);
    if (termsContent) return;
    setTermsLoading(true);
    try {
      const { data } = await supabase
        .from('app_settings')
        .select('value')
        .eq('key', 'page_terms_content')
        .single();
      setTermsContent(data?.value || '');
    } catch {
      setTermsContent('');
    } finally {
      setTermsLoading(false);
    }
  };

  const sendOTP = async () => {
    if (!termsAccepted) {
      setError('يجب الموافقة على الشروط والأحكام أولاً');
      return;
    }
    const normalized = normalizePhone(formData.phone);
    if (!normalized) {
      setError('يرجى إدخال رقم الجوال صحيح');
      return;
    }
    if (!formData.fullName?.trim()) {
      setError('يرجى إدخال الاسم الاول والاخير');
      return;
    }
    if (!formData.email?.trim()) {
      setError('يرجى إدخال البريد الإلكتروني');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setError('يرجى إدخال بريد إلكتروني صحيح');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: existingPhoneProfile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('phone', normalized)
        .maybeSingle();

      if (existingPhoneProfile) {
        if (existingPhoneProfile.role === 'photographer') {
          setError('هذا الرقم مسجل كحساب مصور. لا يمكن استخدامه كعميل.');
        } else if (existingPhoneProfile.role === 'admin') {
          setError('هذا الرقم مسجل كحساب مشرف.');
        } else {
          setError('هذا الرقم مسجل بالفعل. يرجى تسجيل الدخول.');
        }
        setLoading(false);
        return;
      }

      const { data: existingEmailProfile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('email', formData.email.toLowerCase().trim())
        .maybeSingle();
      if (existingEmailProfile) {
        setError('هذا البريد مسجل بالفعل. يرجى استخدام بريد مختلف أو تسجيل الدخول.');
        setLoading(false);
        return;
      }

      const data = await sendOTPApi(normalized);
      if (!data?.success) {
        throw new Error(data?.error || 'فشل إرسال الرمز');
      }
      setMsegatId(data.id);
      setOtpCode('');
      setStep(2);
      setCountdown(60);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'فشل إرسال كود التحقق');
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = useCallback(async (codeOverride) => {
    const code = codeOverride ?? otpCode;
    if (code.length !== 4) {
      setError('يرجى إدخال الكود كاملاً');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const normalized = normalizePhone(formData.phone);
      const data = await verifyOtpAndSignIn({
        phone: normalized,
        code,
        role: 'customer',
        msegatId,
        userData: {
          full_name: formData.fullName?.trim() || null,
          email: formData.email?.trim() || null,
          phone: normalized,
          allow_whatsapp: true,
        },
      });

      if (data?.error) throw new Error(data.error);
      if (data?.redirectUrl) {
        const url = new URL(data.redirectUrl);
        const tokenHash = url.searchParams.get('token');
        if (!tokenHash) throw new Error('فشل الحصول على رمز الدخول');
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: tokenHash,
          type: 'email',
        });
        if (verifyError) throw new Error(verifyError.message);
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          window.gtag('event', 'client_sign_up', { event_category: 'registration', event_label: 'client', value: 1 });
        }
        navigate('/customer-dashboard');
        return;
      }
      if (data?.success) {
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
          window.gtag('event', 'client_sign_up', { event_category: 'registration', event_label: 'client', value: 1 });
        }
        navigate('/customer-dashboard');
        return;
      }
      throw new Error('فشل التحقق');
    } catch (err) {
      console.error('Error:', err);
      setError(err.message || 'كود التحقق غير صحيح');
    } finally {
      setLoading(false);
    }
  }, [otpCode, formData, msegatId, navigate]);

  const handleOtpComplete = useCallback(
    (code) => {
      if (loading || verifyInFlightRef.current) return;
      verifyInFlightRef.current = true;
      verifyOTP(code).finally(() => {
        verifyInFlightRef.current = false;
      });
    },
    [loading, verifyOTP]
  );

  const resendOTP = () => {
    if (countdown > 0) return;
    setOtpCode('');
    sendOTP();
  };

  const normalizedPhone = normalizePhone(formData.phone);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center px-4 py-8" dir="rtl">
      <div className="w-full max-w-md flex flex-col items-start mb-4 self-start">
        <BackButton to="/" label="الرئيسية" className="text-white hover:text-white/90" />
      </div>
      <div className="w-full max-w-md flex-1 flex items-center justify-center py-4">
        <div className="bg-white rounded-3xl shadow-2xl p-8 w-full">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-primary/10 rounded-2xl mb-4 shadow-lg border-2 border-primary/20">
              <AppIcon name="UserPlus" size={40} className="text-primary" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">
              {step === 1 ? 'إنشاء حساب جديد' : 'تأكيد الحساب'}
            </h1>
            <p className="text-slate-500 mt-2">
              {step === 1
                ? 'أدخل بياناتك والتسجيل برسالة نصية'
                : `تم إرسال الكود إلى ${normalizedPhone || formData.phone}`}
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
              <p className="text-sm text-red-600 text-center">{error}</p>
            </div>
          )}

          {step === 1 ? (
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                الاسم الاول والاخير  <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    placeholder="محمد أحمد"
                    className="w-full px-4 py-4 pr-12 rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none transition-colors"
                  />
                  <AppIcon name="User" size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  رقم الجوال <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="05xxxxxxxx"
                    className="w-full px-4 py-4 pr-12 rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none text-left transition-colors"
                    dir="ltr"
                  />
                  <AppIcon name="Phone" size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  البريد الإلكتروني <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="example@email.com"
                    required
                    className="w-full px-4 py-4 pr-12 rounded-xl border-2 border-slate-200 focus:border-primary focus:outline-none text-left transition-colors"
                    dir="ltr"
                  />
                  <AppIcon name="Mail" size={20} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" />
                </div>
              </div>

              {/* ── مربع الموافقة على الشروط والأحكام ── */}
              <div
                className={`rounded-xl border-2 transition-all duration-200 ${
                  termsAccepted
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-slate-200 bg-slate-50'
                }`}
              >
                <label className="flex items-start gap-3 p-4 cursor-pointer select-none">
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={termsAccepted}
                      onChange={(e) => { setTermsAccepted(e.target.checked); setError(''); }}
                    />
                    <div
                      className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200 ${
                        termsAccepted ? 'bg-primary border-primary' : 'bg-white border-slate-300'
                      }`}
                    >
                      {termsAccepted && (
                        <svg viewBox="0 0 12 12" fill="none" className="w-3 h-3" xmlns="http://www.w3.org/2000/svg">
                          <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-sm text-slate-700 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}>
                    أقر بأنني قرأت وأوافق على{' '}
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); openTermsModal(); }}
                      className="text-primary font-semibold underline underline-offset-2 hover:text-primary/80 transition-colors"
                    >
                      الشروط والأحكام
                    </button>
                    {' '}الخاصة بمنصة مشهد
                  </span>
                </label>
              </div>

              <button
                onClick={sendOTP}
                disabled={loading || !normalizedPhone || !formData.fullName?.trim() || !termsAccepted}
                className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <AppIcon name="Loader" size={20} className="animate-spin" />
                ) : (
                  <>
                    <AppIcon name="Send" size={20} />
                    إرسال كود التحقق
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-4 text-center">
                  أدخل كود التحقق المكون من 4 أرقام
                </label>
                <OtpInput
                  value={otpCode}
                  onChange={setOtpCode}
                  onComplete={handleOtpComplete}
                  disabled={loading}
                  autoFocus
                />
              </div>

              <button
                onClick={verifyOTP}
                disabled={loading || otpCode.length !== 4}
                className="w-full py-4 bg-primary text-primary-foreground font-bold rounded-xl hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <AppIcon name="Loader" size={20} className="animate-spin" />
                ) : (
                  <>
                    <AppIcon name="Check" size={20} />
                    تأكيد التسجيل
                  </>
                )}
              </button>

              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-slate-500">
                    إعادة الإرسال بعد <span className="font-bold text-primary">{countdown}</span> ثانية
                  </p>
                ) : (
                  <button
                    onClick={resendOTP}
                    className="text-primary font-bold hover:underline"
                  >
                    إعادة إرسال الكود
                  </button>
                )}
              </div>

              <button
                onClick={() => { setStep(1); setOtpCode(''); setError(''); }}
                className="w-full py-3 text-slate-600 font-medium hover:bg-slate-50 rounded-xl transition-colors"
              >
                تعديل البيانات
              </button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-slate-100 text-center space-y-3">
            <p className="text-slate-600">
              لديك حساب؟{' '}
              <Link to="/customer-login" className="text-primary font-bold hover:underline">
                تسجيل الدخول
              </Link>
            </p>
            <p className="text-slate-600">
              <Link to="/photographer-register" className="text-primary font-bold hover:underline">
                سجل كمصور
              </Link>
            </p>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-slate-400">
            <AppIcon name="Shield" size={16} />
            <p className="text-xs">بياناتك محمية ومشفرة</p>
          </div>
        </div>
      </div>

      {/* ── Modal الشروط والأحكام ─────────────────────────────────────────── */}
      {showTermsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          dir="rtl"
          style={{ fontFamily: 'IBM Plex Sans Arabic, sans-serif' }}
        >
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowTermsModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center">
                  <AppIcon name="FileText" size={18} className="text-primary" />
                </div>
                <h2 className="text-lg font-bold text-slate-900">الشروط والأحكام</h2>
              </div>
              <button
                onClick={() => setShowTermsModal(false)}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              >
                <AppIcon name="X" size={18} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {termsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-3" />
                    <p className="text-sm text-slate-500">جاري التحميل...</p>
                  </div>
                </div>
              ) : termsContent ? (
                <div className="terms-modal-content text-right" dangerouslySetInnerHTML={{ __html: termsContent }} />
              ) : (
                <div className="text-center py-12">
                  <AppIcon name="FileText" size={36} className="text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">لم يتم إضافة محتوى الشروط والأحكام بعد</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
              <button
                onClick={() => { setTermsAccepted(true); setShowTermsModal(false); setError(''); }}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary/90 transition-colors"
              >
                أوافق على الشروط والأحكام
              </button>
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-4 py-2.5 bg-white border border-slate-200 text-slate-600 rounded-xl font-semibold text-sm hover:bg-slate-100 transition-colors"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .terms-modal-content h2 {
          font-size: 1.15rem; font-weight: 700; color: #0f172a;
          margin-top: 1.5rem; margin-bottom: 0.5rem;
          padding-bottom: 0.35rem; border-bottom: 2px solid #e2e8f0;
          font-family: 'IBM Plex Sans Arabic', sans-serif;
        }
        .terms-modal-content h2:first-child { margin-top: 0; }
        .terms-modal-content h3 {
          font-size: 0.95rem; font-weight: 700; color: #1e293b;
          margin-top: 1.1rem; margin-bottom: 0.35rem;
          font-family: 'IBM Plex Sans Arabic', sans-serif;
        }
        .terms-modal-content p {
          color: #475569; line-height: 1.85; margin-bottom: 0.8rem;
          font-size: 0.88rem; font-family: 'IBM Plex Sans Arabic', sans-serif;
        }
        .terms-modal-content ul, .terms-modal-content ol { padding-right: 1.2rem; margin-bottom: 0.8rem; }
        .terms-modal-content li {
          color: #475569; line-height: 1.85; margin-bottom: 0.25rem;
          font-size: 0.88rem; font-family: 'IBM Plex Sans Arabic', sans-serif;
        }
        .terms-modal-content strong { color: #1e293b; font-weight: 700; }
      `}</style>
    </div>
  );
};

export default CustomerRegistration;
